import { useEffect, useMemo, useState } from 'react';
import { IconLayer } from 'deck.gl';
import type { PickingInfo } from 'deck.gl';
import { getImageUsageValue, generateDynamicUsageIcon } from '@/utils/mapUtils.js';
import { useInfrastructureClustering } from '@/hooks/useInfrastructureClustering';
import type { ClusterPoint } from '@/hooks/useInfrastructureClustering';
import { generateClusterIconUrl } from '@/utils/clusterIconGenerator';
import type { DigitalTwinContext, Infrastructure, MapModeContext, MapModeStrategy } from '../mapModes/MapModeStrategy';

export interface SelectedSensor {
	measurementTargetId: number;
	measurementTargetName: string;
	serialNumberMeasurementTool: string | null;
	infrastructureId?: number | null;
	infrastructureTypeId?: number | null;
	sourceId: number | null;
	locationId: string;
}

type UnclusteredIconDatum = Infrastructure & {
	position: [number, number];
	icon: string;
};

type ClusterIconDatum = ClusterPoint & {
	position: [number, number];
	_isCluster: true;
};

type RgbaColor = [number, number, number, number];

interface UseInfrastructureIconLayersParams {
	infras: Infrastructure[] | null | undefined;
	selectedInfrastructureTypes: string[];
	selectedSensors: SelectedSensor[];
	strategy: MapModeStrategy;
	mapContext: MapModeContext;
	digitalTwinContext: DigitalTwinContext;
	zoom: number;
	onIconHover: (info: PickingInfo) => void;
}

export function useInfrastructureIconLayers({
	infras,
	selectedInfrastructureTypes,
	selectedSensors,
	strategy,
	mapContext,
	digitalTwinContext,
	zoom,
	onIconHover,
}: UseInfrastructureIconLayersParams) {
	const [generatedIcons, setGeneratedIcons] = useState<Record<number, string>>({});
	const [iconOpacities, setIconOpacities] = useState<Record<number, number>>({});

	useEffect(() => {
		if (!infras || infras.length === 0) return;

		const targetOpacities = strategy.calculateOpacities(
			infras,
			mapContext,
			digitalTwinContext
		);

		setIconOpacities(targetOpacities);
	}, [
		infras,
		strategy,
		mapContext,
		digitalTwinContext,
	]);

	useEffect(() => {
		if (!infras) return;

		const generateIcons = async () => {
			const typeFilteredInfras = infras.filter((infra) =>
				selectedInfrastructureTypes.includes(infra.infrastructureTypeName)
			);

			const iconPromises = typeFilteredInfras.map(async (infra) => {
				try {
					const iconUrl: string = await generateDynamicUsageIcon(infra, selectedSensors);
					return { id: infra.measurementeInfraestructureId, icon: iconUrl };
				} catch (error) {
					return {
						id: infra.measurementeInfraestructureId,
						icon: getImageUsageValue(infra, selectedSensors) as string,
					};
				}
			});

			const results = await Promise.all(iconPromises);
			const iconsMap = results.reduce((acc: Record<number, string>, { id, icon }) => {
				acc[id] = icon;
				return acc;
			}, {});

			setGeneratedIcons(iconsMap);
		};

		generateIcons();
	}, [infras, selectedInfrastructureTypes, selectedSensors]);

	const filteredInfras = useMemo(() => {
		if (!infras) return [];
		return strategy.filterInfrastructures(
			infras,
			mapContext,
			selectedInfrastructureTypes
		);
	}, [infras, strategy, mapContext, selectedInfrastructureTypes]);

	const visibleInfras = useMemo(() => {
		if (!filteredInfras.length || !Object.keys(iconOpacities).length) return filteredInfras;
		return filteredInfras.filter(
			(infra) => (iconOpacities[infra.measurementeInfraestructureId] ?? 1.0) > 0
		);
	}, [filteredInfras, iconOpacities]);

	const selectedInfraIds = useMemo(() => {
		return new Set<number>(
			(selectedSensors ?? [])
				.map((sensor) => sensor.infrastructureId)
				.filter((id): id is number => id != null),
		);
	}, [selectedSensors]);

	const { clusters, unclustered, getExpansionZoom } = useInfrastructureClustering(
		visibleInfras,
		zoom,
		selectedInfraIds,
	);

	const iconLayers = useMemo(() => {
		const layers: (IconLayer<UnclusteredIconDatum> | IconLayer<ClusterIconDatum>)[] = [];

		const unclusteredData: UnclusteredIconDatum[] = unclustered.map(
			({ infrastructure }: { infrastructure: Infrastructure }) => ({
				...infrastructure,
				position: [parseFloat(infrastructure.longitude), parseFloat(infrastructure.latitude)],
				icon: generatedIcons[infrastructure.measurementeInfraestructureId] ||
					(getImageUsageValue(infrastructure, selectedSensors) as string),
			}),
		);

		layers.push(
			new IconLayer<UnclusteredIconDatum>({
				id: 'icon-layer',
				data: unclusteredData,
				pickable: true,
				parameters: { depthCompare: 'always' },
				getPosition: (d) => d.position,
				getIcon: (d) => ({ url: d.icon, width: 200, height: 200 }),
				sizeScale: 1,
				getSize: Math.max(10, 25 * (zoom / 10)),
				getColor: (d): RgbaColor => {
					const infraId = d.measurementeInfraestructureId;
					const opacity = iconOpacities[infraId] !== undefined ? iconOpacities[infraId] : 1.0;
					const alpha = Math.round(opacity * 255);
					return [255, 255, 255, alpha];
				},
				onHover: onIconHover,
				updateTriggers: {
					getColor: [iconOpacities],
					getIcon: [generatedIcons],
				},
			}),
		);

		if (clusters.length > 0) {
			const clusterData: ClusterIconDatum[] = clusters.map((c) => ({
				...c,
				position: [c.longitude, c.latitude],
				_isCluster: true,
			}));

			layers.push(
				new IconLayer<ClusterIconDatum>({
					id: 'cluster-layer',
					data: clusterData,
					pickable: true,
					parameters: { depthCompare: 'always' },
					getPosition: (d) => d.position,
					getIcon: (d) => ({
						url: generateClusterIconUrl({
							count: d.count,
							normalCount: d.normalCount,
							criticalCount: d.criticalCount,
							noDataCount: d.noDataCount,
							selectedCount: d.selectedCount,
						}),
						width: 200,
						height: 200,
					}),
					sizeScale: 1,
					getSize: (d) => {
						const baseSize = Math.max(15, 30 * (zoom / 10));
						const countScale = 1 + Math.min(1.4, Math.log2(Math.max(1, d.count)) * 0.26);
						return baseSize * countScale;
					},
					updateTriggers: {
						getIcon: [clusters],
						getSize: [zoom],
					},
				}),
			);
		}

		return layers;
	}, [
		unclustered,
		clusters,
		zoom,
		selectedSensors,
		iconOpacities,
		generatedIcons,
		onIconHover,
	]);

	return { iconLayers, iconOpacities, getExpansionZoom };
}
